// src/hooks/useSeo.js
import { useEffect } from "react";
import { apiRequest } from "../services/api";

export const useSeo = (pageKey, entityId = null, schema = null) => {
  useEffect(() => {
    apiRequest(
      entityId ? `/seo/${pageKey}?entity=${entityId}` : `/seo/${pageKey}`
    )
      .then((seo) => {
        if (!seo) return;

        // ===== TITLE =====
        if (seo.title) {
          document.title = seo.title;
          setMeta("og:title", seo.title, "property");
        }

        // ===== DESCRIPTION =====
        if (seo.meta_description) {
          setMeta("description", seo.meta_description);
          setMeta("og:description", seo.meta_description, "property");
        }

        // ===== KEYWORDS =====
        setMeta("keywords", seo.meta_keywords);

        // ===== CANONICAL =====
        if (seo.canonical) {
          setLink("canonical", seo.canonical);
          setMeta("og:url", seo.canonical, "property");
        }

        // ===== ROBOTS =====
        setMeta(
          "robots",
          seo.is_indexable ? "index,follow" : "noindex,nofollow"
        );

        // ===== OPEN GRAPH =====
        setMeta("og:type", seo.og_type || "website", "property");
        setMeta(
          "og:image",
          seo.og_image || "/images/og-cover.jpg",
          "property"
        );

        // ===== TWITTER =====
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", seo.title);
        setMeta("twitter:description", seo.meta_description);
        setMeta("twitter:image", seo.og_image || "/images/og-cover.jpg");

        // ===== HREFLANG =====
        if (seo.hreflang) {
          Object.entries(seo.hreflang).forEach(([lang, url]) => {
            setAltLink("alternate", url, lang);
          });
        }

        // ===== STRUCTURED DATA (OPTIONAL) =====
        if (schema) {
          injectSchema(schema);
        }
      })
      .catch(() => {});
  }, [pageKey, entityId]);
};

/* ================= HELPERS ================= */

const setMeta = (name, content, attr = "name") => {
  if (!content) return;
  let el = document.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
};

const setAltLink = (rel, href, lang) => {
  let el = document.querySelector(
    `link[rel="${rel}"][hreflang="${lang}"]`
  );
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    el.hreflang = lang;
    document.head.appendChild(el);
  }
  el.href = href;
};

const injectSchema = (schema) => {
  let el = document.getElementById("seo-schema");
  if (!el) {
    el = document.createElement("script");
    el.type = "application/ld+json";
    el.id = "seo-schema";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema);
};
